import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { CheckCircleOutline, ErrorOutline, ExpandMore, RadioButtonUnchecked } from "@mui/icons-material";
import { InkyWorking } from "../../components/brand/InkyMascot";

const TRACE_OPEN_KEY = "quillai-inky-trace-open";

/**
 * Read a boolean preference (localStorage may be unavailable).
 * @param {string} key - Storage key.
 * @returns {boolean} Stored value.
 */
const readFlag = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

/**
 * Write a boolean preference (best effort).
 * @param {string} key - Storage key.
 * @param {boolean} value - Value.
 * @returns {void}
 */
const writeFlag = (key, value) => {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // Preference only.
  }
};

/**
 * Seconds/milliseconds for a duration.
 * @param {number} ms - Milliseconds.
 * @returns {string} e.g. "4.2s" or "1m 05s".
 */
export const formatDuration = (ms) => {
  const value = Math.max(0, Number(ms) || 0);
  if (value < 60_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)}s`;
  const minutes = Math.floor(value / 60_000);
  const seconds = Math.round((value % 60_000) / 1000);
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
};

/**
 * One step row.
 * @param {{step: object}} props - Step.
 * @returns {JSX.Element} Row.
 */
function StepRow({ step }) {
  const Icon = step.status === "running" ? RadioButtonUnchecked : step.ok === false || step.status === "error" ? ErrorOutline : CheckCircleOutline;
  const color = step.status === "running" ? "var(--q-accent, #4F3BD6)" : step.ok === false || step.status === "error" ? "var(--q-danger, #D63B1F)" : "var(--q-success, #1B7F45)";
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, py: 0.25 }}>
      <Icon sx={{ fontSize: 14, mt: "2px", color, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" component="div" sx={{ color: "var(--q-text-2, inherit)", lineHeight: 1.4 }}>
          {step.label}
        </Typography>
        {step.summary && step.status !== "running" && (
          <Typography variant="caption" component="div" sx={{ color: "var(--q-muted, inherit)", lineHeight: 1.35 }}>
            {step.summary}
            {step.ms ? ` · ${formatDuration(step.ms)}` : ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Disclosure toggle shared by the live view and the saved strip.
 * @param {{open: boolean, onToggle: () => void, children: React.ReactNode, controls: string}} props - Props.
 * @returns {JSX.Element} Toggle.
 */
function TraceToggle({ open, onToggle, children, controls }) {
  return (
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        px: 0.75,
        py: 0.25,
        borderRadius: "var(--q-radius-sm, 6px)",
        color: "var(--q-muted, inherit)",
        fontSize: "0.75rem",
        "&:hover": { bgcolor: "var(--q-hover, rgba(0,0,0,0.04))" },
        "&.Mui-focusVisible": { outline: "2px solid var(--q-focus-ring, #4F3BD6)", outlineOffset: 1 },
      }}
    >
      {children}
      <ExpandMore sx={{ fontSize: 16, transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
    </ButtonBase>
  );
}

/**
 * Live working state of a running turn: one status line with the running step's human label
 * and elapsed time, plus the steps behind a disclosure (open state remembered).
 * @param {{turn: object}} props - Running turn from the registry.
 * @returns {JSX.Element} Working view.
 */
export function LiveWorking({ turn }) {
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(() => readFlag(TRACE_OPEN_KEY));

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const steps = turn.live.steps;
  const running = [...steps].reverse().find((step) => step.status === "running");
  const done = steps.filter((step) => step.status !== "running").length;
  const label = turn.stopping
    ? "Stopping after the current step"
    : turn.status === "detached"
      ? "The connection dropped. Still working, waiting for the reply"
      : running?.label || (turn.live.phase === "writing" ? "Writing" : steps.length ? "Putting it together" : "Thinking");
  const elapsed = formatDuration(now - turn.startedAt);
  const listId = `inky-steps-${turn.key}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <InkyWorking label={label} mood={turn.live.phase === "writing" ? "writing" : "thinking"} size={24} />
        <Typography variant="caption" sx={{ color: "var(--q-faint, inherit)" }}>
          {elapsed}
        </Typography>
        {steps.length > 0 && (
          <TraceToggle
            open={open}
            controls={listId}
            onToggle={() => {
              setOpen((value) => {
                writeFlag(TRACE_OPEN_KEY, !value);
                return !value;
              });
            }}
          >
            {done} step{done === 1 ? "" : "s"} done
          </TraceToggle>
        )}
      </Box>
      <Collapse in={open && steps.length > 0} id={listId}>
        <Box sx={{ pl: 1, ml: 1.5, borderLeft: "2px solid var(--q-border-subtle, #ECE9E4)" }}>
          {steps.map((step) => (
            <StepRow key={step.id} step={step} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Collapsed "N steps · 4.2s" strip on a saved reply; expands to the step list.
 * @param {{steps: Array<object>, durationMs?: number, messageId: string}} props - Saved trace.
 * @returns {JSX.Element|null} Strip.
 */
export function TraceStrip({ steps, durationMs, messageId }) {
  const [open, setOpen] = useState(false);
  if (!steps?.length) return null;
  const listId = `inky-trace-${messageId}`;
  const failed = steps.filter((step) => step.ok === false).length;
  return (
    <Box sx={{ mb: 0.5 }}>
      <TraceToggle open={open} controls={listId} onToggle={() => setOpen((value) => !value)}>
        {steps.length} step{steps.length === 1 ? "" : "s"}
        {durationMs ? ` · ${formatDuration(durationMs)}` : ""}
        {failed ? ` · ${failed} failed` : ""}
      </TraceToggle>
      <Collapse in={open} id={listId}>
        <Box sx={{ pl: 1, ml: 1, mt: 0.5, borderLeft: "2px solid var(--q-border-subtle, #ECE9E4)" }}>
          {steps.map((step) => (
            <StepRow key={step.id || step.label} step={{ ...step, status: step.ok === false ? "error" : "done" }} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
